import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { useAppDispatch } from "src/store";
import {
  approveOnboardingDocument,
  getOnboardingDetail,
  rejectOnboardingDocument,
  transitionOnboarding,
} from "src/store/client-onboarding/saga";
import {
  ONBOARDING_STATUS_COLOR,
  ONBOARDING_STATUS_LABEL,
  OnboardingDetail,
  RISK_LANE_COLOR,
} from "src/store/client-onboarding/type";
import { GetLocaleDate } from "src/helpers/getLocaleDate";

interface Props {
  clientId: string;
  open: boolean;
  onHide: () => void;
  onChanged: () => void;
}

/**
 * Onboarding inceleme paneli — künye, belgeler, risk ve karar.
 *
 * Buton listesi backend'in döndürdüğü `AllowedTransitions`'tan üretilir;
 * ekran kendi başına geçiş kuralı bilmez, tek doğruluk kaynağı durum makinesi.
 */
const OnboardingReviewDrawer = ({ clientId, open, onHide, onChanged }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [detail, setDetail] = useState<OnboardingDetail>();
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string>();

  const load = () => {
    setLoading(true);
    setError(undefined);
    dispatch(
      getOnboardingDetail({
        id: clientId,
        payload: {
          onSuccess: (_m: any, p: OnboardingDetail) => {
            setDetail(p);
            setLoading(false);
          },
          onError: (m: string) => {
            setError(m);
            setLoading(false);
          },
          onFinally: () => setLoading(false),
        },
      })
    );
  };

  useEffect(() => {
    if (open) load();
  }, [open, clientId]);

  const doTransition = (toStatusId: number) => {
    // Ret gerekçesi backend'de de zorunlu; burada erken uyarıyoruz.
    if (toStatusId === 6 && !note.trim()) {
      setError(t("ClientOnboarding.RejectNeedsNote", "Ret için gerekçe zorunludur.") as string);
      return;
    }
    setError(undefined);
    dispatch(
      transitionOnboarding({
        id: clientId,
        body: { ToStatusId: toStatusId, Note: note.trim() || null },
        payload: {
          onSuccess: () => {
            setNote("");
            load();
            onChanged();
          },
          onError: (m: string) => setError(m),
        },
      })
    );
  };

  const approveDoc = (docId: string) =>
    dispatch(
      approveOnboardingDocument({
        id: docId,
        payload: { onSuccess: () => load(), onError: (m: string) => setError(m) },
      })
    );

  const rejectDoc = (docId: string) => {
    const reason = window.prompt(t("ClientOnboarding.DocRejectReason", "Ret gerekçesi") as string);
    if (!reason || !reason.trim()) return;
    dispatch(
      rejectOnboardingDocument({
        id: docId,
        body: { Reason: reason.trim() },
        payload: { onSuccess: () => load(), onError: (m: string) => setError(m) },
      })
    );
  };

  const c = detail?.Client;
  const risk = detail?.Risk;

  return (
    <Modal isOpen={open} toggle={onHide} size="lg" centered>
      <ModalHeader toggle={onHide}>
        {t("ClientOnboarding.ReviewTitle", "Onboarding İncelemesi")}
        {c ? ` — ${c.ClientName ?? ""}` : ""}
      </ModalHeader>
      <ModalBody>
        {loading && <div className="text-center py-4">{t("Components.Loading", "Yükleniyor…")}</div>}

        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && c && (
          <>
            {/* Künye */}
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
              <span className={`badge bg-${ONBOARDING_STATUS_COLOR[c.OnboardingStatusId] ?? "secondary"}`}>
                {ONBOARDING_STATUS_LABEL[c.OnboardingStatusId] ?? c.OnboardingStatus}
              </span>
              {risk && (
                <span className={`badge bg-${RISK_LANE_COLOR[risk.Lane] ?? "secondary"}`}>
                  {t("ClientOnboarding.Risk", "Risk")}: {risk.Score} · {risk.Lane}
                </span>
              )}
              <span className="badge bg-light text-dark">
                {t("ClientOnboarding.TrustTier", "Güven")}: {c.TrustTier}
              </span>
              {c.QualityFloorPass && (
                <span className="badge bg-success">
                  {t("ClientOnboarding.InSearch", "Aramada görünür")}
                </span>
              )}
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <table className="table table-sm mb-0">
                  <tbody>
                    <tr>
                      <td className="text-muted">{t("ClientOnboarding.Sector", "Sektör")}</td>
                      <td>{c.SectorName ?? "—"}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">{t("ClientOnboarding.TaxNumber", "Vergi No")}</td>
                      <td>{c.TaxNumber ?? "—"}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">{t("ClientOnboarding.TaxOffice", "Vergi Dairesi")}</td>
                      <td>{c.TaxOffice ?? "—"}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">{t("ClientOnboarding.Authorized", "Yetkili")}</td>
                      <td>{c.AuthorizedPerson || "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="col-md-6">
                <table className="table table-sm mb-0">
                  <tbody>
                    <tr>
                      <td className="text-muted">{t("ClientOnboarding.Email", "E-posta")}</td>
                      <td>
                        {c.Email ?? "—"}{" "}
                        {c.EmailValidated && <i className="mdi mdi-check-circle text-success" />}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">{t("ClientOnboarding.Phone", "Telefon")}</td>
                      <td>
                        {c.Phone ?? "—"}{" "}
                        {c.PhoneValidated && <i className="mdi mdi-check-circle text-success" />}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">IBAN</td>
                      <td>{c.Iban ?? <span className="text-warning">girilmemiş</span>}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">{t("ClientOnboarding.SubmittedAt", "Başvuru")}</td>
                      <td>{c.SubmittedAt ? GetLocaleDate(c.SubmittedAt) : "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Risk gerekçeleri — kararın dayanağı */}
            {risk && risk.Reasons?.length > 0 && (
              <div className={`alert alert-${RISK_LANE_COLOR[risk.Lane] ?? "secondary"} py-2`}>
                <ul className="mb-0 ps-3">
                  {risk.Reasons.map((reason, i) => (
                    <li key={i}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Belgeler */}
            <h6 className="mt-3">{t("ClientOnboarding.Documents", "Belgeler")}</h6>
            {detail!.Documents.length === 0 ? (
              <p className="text-muted">{t("ClientOnboarding.NoDocuments", "Henüz belge yüklenmemiş.")}</p>
            ) : (
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>{t("ClientOnboarding.DocType", "Tür")}</th>
                    <th>{t("ClientOnboarding.DocStatus", "Durum")}</th>
                    <th>{t("ClientOnboarding.DocUploaded", "Yüklenme")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detail!.Documents.map((d) => (
                    <tr key={d.Id}>
                      <td>
                        {d.Location ? (
                          <a href={d.Location} target="_blank" rel="noreferrer">
                            {d.TypeName ?? d.TypeId ?? "?"}
                          </a>
                        ) : (
                          d.TypeName ?? d.TypeId ?? "?"
                        )}
                      </td>
                      <td>
                        {d.Approved && <span className="badge bg-success">onaylı</span>}
                        {d.Rejected && (
                          <span className="badge bg-danger" title={d.RejectReason ?? ""}>
                            reddedildi
                          </span>
                        )}
                        {!d.Approved && !d.Rejected && <span className="badge bg-secondary">bekliyor</span>}
                      </td>
                      <td>{d.UploadedAt ? GetLocaleDate(d.UploadedAt) : "—"}</td>
                      <td className="text-end">
                        {!d.Approved && (
                          <button className="btn btn-sm btn-success me-1" onClick={() => approveDoc(d.Id)}>
                            <i className="mdi mdi-check" />
                          </button>
                        )}
                        {!d.Rejected && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => rejectDoc(d.Id)}>
                            <i className="mdi mdi-close" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Karar */}
            <h6 className="mt-4">{t("ClientOnboarding.Decision", "Karar")}</h6>
            <textarea
              className="form-control mb-2"
              rows={2}
              placeholder={t("ClientOnboarding.NotePlaceholder", "Not / ret gerekçesi") as string}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="d-flex flex-wrap gap-2">
              {c.AllowedTransitions.map((to) => (
                <button
                  key={to}
                  className={`btn btn-${to === 6 ? "danger" : to === 7 || to === 4 ? "success" : "primary"}`}
                  onClick={() => doTransition(to)}
                >
                  {ONBOARDING_STATUS_LABEL[to] ?? to}
                </button>
              ))}
              {c.AllowedTransitions.length === 0 && (
                <span className="text-muted">
                  {t("ClientOnboarding.NoTransition", "Bu durumdan geçiş tanımlı değil.")}
                </span>
              )}
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default OnboardingReviewDrawer;
